<template>
  <div class="host-room cabinet-card">
    <div class="d-flex align-center flex-wrap host-room__head">
      <h2 class="cabinet-card__title">{{ t("title") }}</h2>
      <span class="host-room__status" :class="{ 'host-room__status--open': room.is_open }">
        {{ room.is_open ? t("opened") : t("closed") }}
      </span>
    </div>
    <p class="host-room__hint">{{ t("hint") }}</p>

    <template v-if="room.url">
      <div class="host-room__label">{{ t("link") }}:</div>
      <div class="host-room__link">
        <a :href="room.url" target="_blank" rel="noopener" class="host-room__url" @click="guardLink">{{ room.url }}</a>
        <button type="button" class="host-room__copy" @click="copyLink">{{ t("copy").toLowerCase() }}</button>
      </div>
      <button type="button" class="host-room__regenerate" :disabled="busy" @click="regenerate">{{ t("generateNew") }}</button>
    </template>

    <VideoCall
      v-if="room.is_open"
      class="mt-6"
      :local-stream="localStream"
      :remote-stream="remoteStream"
      :status="statusText"
      :waiting="!remoteStream"
    >
      <template #actions>
        <v-btn v-if="room.guest_online" small rounded color="warning" @click="kick">
          {{ t("kick") }}
        </v-btn>
        <v-btn small rounded color="error" @click="close">
          <v-icon left small>mdi-phone-off</v-icon>{{ t("close") }}
        </v-btn>
      </template>
    </VideoCall>

    <p v-if="mediaError" class="error--text mt-3 mb-0">{{ t("cameraError") }}</p>

    <div v-if="!room.is_open" class="host-room__open-wrap">
      <v-btn
        class="host-room__open"
        color="primary"
        depressed
        :loading="busy"
        :disabled="!room.token"
        @click="open"
      >
        <v-icon left>mdi-play-circle-outline</v-icon>{{ t("open") }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import Vue from "vue";
import RoomServices from "@/services/api/room.services";
import VideoCall from "./VideoCall";
import { RoomPeer, getLocalStream } from "./roomPeer";
import { isSubscriptionLocked } from "@/utils/subscriptionLock";

const POLL_FAST = 1000;
const POLL_SLOW = 2500;

export default {
  name: "HostRoom",
  components: { VideoCall },
  data() {
    return {
      room: {},
      busy: false,
      localStream: null,
      remoteStream: null,
      mediaError: false,
      peer: null,
      guestId: null,
      lastSignalId: 0,
      pollTimer: null,
      connected: false,
      destroyed: false,
    };
  },
  computed: {
    statusText() {
      if (this.room.guest_online) {
        return this.connected ? this.t("guestJoined") : this.t("connecting");
      }
      return this.t("waiting");
    },
  },
  mounted() {
    window.addEventListener("beforeunload", this.onUnload);
    RoomServices.hostShow().then((response) => {
      this.room = response.data.data;
      // page reload while the room was open: start a fresh session, the client reconnects by itself
      if (this.room.is_open && !this.locked()) {
        this.open();
      }
    });
  },
  beforeDestroy() {
    this.destroyed = true;
    window.removeEventListener("beforeunload", this.onUnload);
    this.stopSession();
  },
  methods: {
    t(key) {
      return this.$vuetify.lang.t(`$vuetify.main.psychologistDashboard.videoRoom.${key}`);
    },
    toast(text, icon = "success") {
      Vue.swal({
        target: document.getElementById("main"),
        text,
        icon,
        timer: 3000,
        toast: true,
        position: "top-right",
        showConfirmButton: false,
      });
    },
    // unpaid psychologist: every room button explains how to activate the subscription instead
    locked() {
      if (!isSubscriptionLocked()) return false;
      this.$root.$emit("subscription-locked");
      return true;
    },
    guardLink(event) {
      if (this.locked()) event.preventDefault();
    },
    copyLink() {
      if (this.locked()) return;
      navigator.clipboard.writeText(this.room.url).then(() => this.toast(this.t("copied")));
    },
    async open() {
      if (this.locked()) return;
      this.busy = true;
      this.mediaError = false;
      try {
        if (!this.localStream) {
          this.localStream = await getLocalStream();
        }
      } catch (e) {
        this.mediaError = true;
        this.busy = false;
        return;
      }

      RoomServices.hostOpen()
        .then((response) => {
          this.room = response.data.data;
          this.lastSignalId = 0;
          this.schedulePoll(0);
        })
        .finally(() => {
          this.busy = false;
        });
    },
    close() {
      this.busy = true;
      RoomServices.hostClose()
        .then((response) => {
          this.room = response.data.data;
        })
        .finally(() => {
          this.busy = false;
          this.stopSession();
        });
    },
    kick() {
      RoomServices.hostKick().then((response) => {
        this.room = response.data.data;
        this.dropPeer();
      });
    },
    regenerate() {
      if (this.locked()) return;
      if (!window.confirm(this.t("regenerateConfirm"))) return;
      this.busy = true;
      RoomServices.hostRegenerate()
        .then((response) => {
          this.room = response.data.data;
          this.stopSession();
        })
        .finally(() => {
          this.busy = false;
        });
    },
    schedulePoll(delay) {
      clearTimeout(this.pollTimer);
      if (this.destroyed || !this.room.is_open) return;
      this.pollTimer = setTimeout(this.poll, delay);
    },
    poll() {
      RoomServices.hostPoll(this.lastSignalId)
        .then((response) => {
          const { room, signals } = response.data;
          this.room = room;
          signals.forEach((signal) => {
            this.lastSignalId = Math.max(this.lastSignalId, signal.id);
            this.handleSignal(signal);
          });
          if (!room.guest_online && this.peer) {
            this.dropPeer();
          }
        })
        .catch(() => {})
        .finally(() => {
          this.schedulePoll(this.connected ? POLL_SLOW : POLL_FAST);
        });
    },
    handleSignal({ guest_id: guestId, payload }) {
      if (payload.type === "guest_joined") {
        this.startPeer(guestId);
        return;
      }
      if (payload.type === "guest_left") {
        if (guestId === this.guestId) this.dropPeer();
        return;
      }
      if (this.peer && guestId === this.guestId) {
        this.peer.handle(payload);
      }
    },
    startPeer(guestId) {
      this.dropPeer();
      this.guestId = guestId;
      this.peer = new RoomPeer({
        initiator: true,
        stream: this.localStream,
        send: (payload) => RoomServices.hostSignal(guestId, payload).catch(() => {}),
        onStream: (stream) => {
          this.remoteStream = stream;
        },
        onConnect: () => {
          this.connected = true;
        },
        onClose: () => {
          if (this.guestId === guestId) {
            this.remoteStream = null;
            this.connected = false;
          }
        },
      });
    },
    dropPeer() {
      if (this.peer) this.peer.destroy();
      this.peer = null;
      this.guestId = null;
      this.remoteStream = null;
      this.connected = false;
    },
    stopSession() {
      clearTimeout(this.pollTimer);
      this.dropPeer();
      if (this.localStream) {
        this.localStream.getTracks().forEach((track) => track.stop());
        this.localStream = null;
      }
    },
    onUnload() {
      if (this.room.is_open) {
        // the room stays open for the client; the host reopens it on return
        this.stopSession();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.host-room__head {
  column-gap: 24px;
  row-gap: 8px;
}
.host-room__status {
  padding: 3px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #FFFFFF;
  background: #A9A9A9;
  border-radius: 3px;
}
.host-room__status--open {
  background: #7DC592;
}
.host-room__hint {
  margin: 34px 0 0;
  max-width: 670px;
  font-size: 16px;
  line-height: 1.2;
  color: #575756;
}
.host-room__label {
  margin-top: 24px;
  font-size: 14px;
  color: #8A8A8A;
}
.host-room__link {
  display: flex;
  align-items: center;
  column-gap: 16px;
  margin-top: 4px;
  padding: 10px 10px 10px 20px;
  background: #DCE5EC;
  border: 1px solid #C9D6E0;
  border-radius: 4px;
}
.host-room__url {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  color: #00A3B7 !important;
  word-break: break-all;
}
.host-room__copy {
  flex-shrink: 0;
  padding: 5px 17px;
  font-size: 15px;
  color: #FFFFFF;
  background: #A9A9A9;
  border-radius: 3px;
}
.host-room__copy:hover {
  background: #969696;
}
.host-room__regenerate {
  margin-top: 8px;
  font-size: 14px;
  color: #575756;
}
.host-room__regenerate:hover {
  color: #00A3B7;
}
.host-room__open-wrap {
  display: flex;
  justify-content: center;
  margin-top: 80px;
}
.host-room__open.v-btn {
  height: 40px !important;
  padding: 0 26px !important;
  font-size: 18px;
  letter-spacing: normal;
  text-transform: none;
  border-radius: 3px;
}
@media (max-width: 600px) {
  .host-room__link {
    flex-direction: column;
    align-items: stretch;
    row-gap: 10px;
  }
  .host-room__open-wrap {
    margin-top: 40px;
  }
}
</style>
